import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { BACKEND_URL } from "@/lib/backendUrl";

const API = `${BACKEND_URL}/api`;
const AuthContext = createContext(null);

// Session lives in an httpOnly cookie set by the backend — nothing is stored in localStorage
axios.defaults.withCredentials = true;
axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest"; // CSRF guard expected by the backend
localStorage.removeItem("hlh_token"); // clean up sessions from the previous token-based version

export function formatApiError(detail, fallback = "Something went wrong") {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail.msg || String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out

  const logout = useCallback(() => {
    setUser(false);
    axios.post(`${API}/auth/logout`).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/auth/me`).then(({ data }) => setUser(data.user)).catch(() => setUser(false));
  }, []);

  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err.response?.status === 401 && !/\/auth\/(login|me|logout)/.test(err.config?.url || "")) {
          logout(); // session expired: redirect to login and swallow the error so pages don't crash
          return new Promise(() => {});
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [logout]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setUser(data.user);
    return data.user;
  };

  const workerLogin = async (username, password) => {
    const { data } = await axios.post(`${API}/freelance/portal/login`, { username, password });
    setUser(data.user);
    return data.user;
  };

  const value = useMemo(() => ({ user, login, workerLogin, logout, isOwner: user?.role === "owner", isWorker: user?.role === "freelance_worker", canAccounts: user?.role === "owner" || user?.role === "accounts", canHR: !!user?.hr_access }), [user]); // eslint-disable-line react-hooks/exhaustive-deps
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
